import { Link } from "@tanstack/react-router"
import { AlertTriangle, ShieldAlert, Sprout } from "lucide-react"

import { useDashboardStore } from "@/store/dashboard"
import {
  NavigationLink,
  SectionHeader,
  type NavigationItem,
} from "@/components/navigation/sidebar"

import { useMonitoringStatus, type FacilityScope } from "../queries"

// Poll the nav status at the same cadence the guide specifies for the badge.
const NAV_POLL_MS = 30 * 1000

/** Lowest (most urgent) tier number with open alerts; tier 1 is most severe. */
function mostUrgentOpenTier(
  openByTier: { tier: number; count: number }[] | undefined
) {
  const open = (openByTier ?? []).filter(t => t.count > 0).map(t => t.tier)
  return open.length ? Math.min(...open) : undefined
}

/**
 * Oncology monitoring entry points in the main sidebar.
 *
 * Note we do NOT hide the section when the facility's `enabled` is false — the
 * escalation config screens live here and must work *before* switch-on, so an
 * un-enabled facility sees the section marked "Setup" instead. Only
 * `status.enabled` reflects the live patient-facing gate.
 */
export function OncologyNavSection({ isCollapsed }: { isCollapsed: boolean }) {
  // facilityId is optional for single-facility staff (backend resolves it);
  // for multi-facility staff we scope to whatever they picked in the dashboard.
  const selectedFacility = useDashboardStore(s => s.selectedFacility)
  const facilityId = Number(selectedFacility)
  const scope: FacilityScope | undefined = Number.isFinite(facilityId)
    ? { facilityId }
    : undefined

  const { data: status } = useMonitoringStatus(scope, {
    refetchInterval: NAV_POLL_MS,
  })

  const openAlertCount = status?.openAlertCount ?? 0
  const needsSetup = status ? !status.enabled : false
  const needsSignOff = status ? !status.signedOff : false

  const items: NavigationItem[] = [
    {
      to: "/oncology/triage",
      icon: AlertTriangle,
      label: "Triage",
      badge:
        openAlertCount > 0
          ? {
              label: String(openAlertCount),
              variant: "alert",
              tier: mostUrgentOpenTier(status?.openByTier),
            }
          : undefined,
    },
    {
      to: "/oncology/escalation",
      icon: ShieldAlert,
      label: "Escalation Config",
    },
    {
      to: "/oncology/survivorship",
      icon: Sprout,
      label: "Survivorship",
    },
  ]

  return (
    <div>
      <SectionHeader
        title="Oncology"
        isCollapsed={isCollapsed}
        action={
          needsSetup ? (
            <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-warning">
              Setup
            </span>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-y-1" role="menu">
        {items.map(item => (
          <NavigationLink key={item.to} item={item} isCollapsed={isCollapsed} />
        ))}

        {/* Finish-setup affordance: the policy must be signed off before the
            facility goes live. Points at the escalation config. */}
        {needsSignOff && !isCollapsed && (
          <Link
            to="/oncology/escalation"
            className="mx-1 mt-1 flex items-center gap-2 rounded-lg bg-warning/10 px-3 py-2 text-xs font-medium text-warning transition-colors hover:bg-warning/20"
          >
            <ShieldAlert className="size-3.5 shrink-0" />
            Finish setup — sign off escalation policy
          </Link>
        )}
      </div>
    </div>
  )
}
